import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { postData, fetchData } from '../../../apiService';
import Select from 'react-select';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import '../Customer/Customer.css';

const PaymentCreate = () => {
    const [isLoading, setIsLoading] = useState(false);
    const history = useHistory();
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId, token, logout]);

    const [customerOptions, setCustomerOptions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [invoiceDetails, setInvoiceDetails] = useState({});
    const [cleaningCharge, setCleaningCharge] = useState('0');
    const [otherCharge, setOtherCharge] = useState('0');
    const [selectedPaymentType, setSelectedPaymentType] = useState('1');

    const [formData, setFormData] = useState({
        CompanyId: companyId,
        BillDate: '',
        BillNo: 0,
        CustomerId: '',
        PaymentType: '1',
        Remarks: '',
        createdBy: userId,
    });

    useEffect(() => {
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        setFormData((prevData) => ({
            ...prevData,
            BillDate: formattedDate,
            PaymentType: '1',
        }));
    }, []);

    useEffect(() => {
        fetchCustomerData();
    }, []);

    const fetchCustomerData = () => {
        fetchData('ReceiptApi', `/Party/${companyId}`, token)
            .then((response) => response.response_data)
            .then((data) => {
                const options = data.map((customer) => ({
                    label: customer.Name,
                    value: customer.ID,
                }));
                setCustomerOptions(options);
            })
            .catch((error) => {
                console.error('Error fetching customer data:', error);
            });
    };

    const handleCustomerSelect = (selectedOption) => {
        setSelectedCustomer(selectedOption);
        setFormData((prevData) => ({
            ...prevData,
            CustomerId: selectedOption ? selectedOption.value : '',
        }));

        if (selectedOption) {
            fetchData('ReceiptApi', `/PartyPaymentBill/${selectedOption.value}/${companyId}`, token)
                .then((response) => {
                    if (response.responseCode === '1111') {
                        setInvoiceDetails(response.response_data);
                    }
                })
                .catch((error) => {
                    console.error('Error fetching invoice details:', error);
                });
        } else {
            setInvoiceDetails({});
        }
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));

        if (name === 'PaymentType') {
            setSelectedPaymentType(value);
        }
    };

    const formatDateForInput = (dateString) => {
        const currentDate = new Date(dateString);
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const refundAmount = (invoiceDetails.CautionDeposite || 0) - (parseFloat(cleaningCharge) || 0) - (parseFloat(otherCharge) || 0);

    const submitData = async () => {
        setIsLoading(true);

        if (!invoiceDetails.ID || invoiceDetails.ID === 0) {
            setErrorMessage('Customer is required!');
            setIsLoading(false);
            return;
        }

        const createData = {
            CompanyId: companyId,
            BillDate: formData.BillDate,
            CustomerId: invoiceDetails.ID,
            RoomId: invoiceDetails.RoomId,
            PaymentType: formData.PaymentType,
            CautionDeposut: invoiceDetails.CautionDeposite,
            CleaningCharge: cleaningCharge,
            OtherCharge: otherCharge,
            RefundAmount: refundAmount,
            Remarks: formData.Remarks,
            CreatedBy: userId,
        };

        try {
            await postData('CautionRefundApi', createData, token);
            setSuccessMessage('Payment Created successfully!');
            setErrorMessage('');
            history.push('/PaymentIndex');
        } catch (error) {
            console.error('Payment Create error:', error);
            setErrorMessage('Failed to create payment. Please try again.');
            setIsLoading(false);
        }
    };

    return (
        <div className="form-container">
            <div className="form-card">
                {/* Header */}
                <div className="form-header">
                    <h1 className="form-title">Create Payment Refund</h1>
                    <div className="form-actions">
                        <Link to="/PaymentIndex" className="btn-back">
                            <ArrowBackIcon fontSize="small" />
                            Back to List
                        </Link>
                        <button className="btn-save" onClick={submitData} disabled={isLoading}>
                            {isLoading ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                <>
                                    <SaveIcon fontSize="small" />
                                    Save
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Messages */}
                {errorMessage && <div className="message error" style={{ margin: '16px 20px 0' }}>{errorMessage}</div>}
                {successMessage && <div className="message success" style={{ margin: '16px 20px 0' }}>{successMessage}</div>}

                {/* Form Body */}
                <div className="form-body">
                    {/* Row 1 */}
                    <div className="form-row">
                        <div className="form-group" style={{ flex: 2 }}>
                            <label className="form-label">Customer *</label>
                            <Select
                                className="form-select"
                                options={customerOptions}
                                value={selectedCustomer}
                                onChange={handleCustomerSelect}
                                placeholder="Select a customer"
                                isClearable
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Bill Date *</label>
                            <input
                                className="form-input"
                                value={formatDateForInput(formData.BillDate)}
                                type="date"
                                name="BillDate"
                                onChange={handleChange}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Bill Number</label>
                            <input
                                className="form-input"
                                value={formData.BillNo || 'Auto Generated'}
                                type="text"
                                readOnly
                                style={{ backgroundColor: '#f8fafc' }}
                            />
                        </div>
                    </div>

                    {/* Row 2 */}
                    <div className="form-row">
                        <div className="form-group" style={{ flex: 2 }}>
                            <label className="form-label">Remarks</label>
                            <input
                                className="form-input"
                                value={formData.Remarks}
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter remarks"
                                name="Remarks"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Caution Deposit</label>
                            <input
                                className="form-input"
                                value={invoiceDetails.CautionDeposite !== undefined ? parseFloat(invoiceDetails.CautionDeposite).toFixed(2) : '0.00'}
                                type="text"
                                readOnly
                                style={{ textAlign: 'right', backgroundColor: '#f8fafc' }}
                            />
                        </div>
                    </div>

                    {/* Row 3 - Charges */}
                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">Cleaning Charge</label>
                            <input
                                className="form-input"
                                value={cleaningCharge}
                                onChange={(e) => setCleaningCharge(e.target.value)}
                                type="text"
                                placeholder="Enter cleaning charge"
                                style={{ textAlign: 'right' }}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Other Charge</label>
                            <input
                                className="form-input"
                                value={otherCharge}
                                onChange={(e) => setOtherCharge(e.target.value)}
                                type="text"
                                placeholder="Enter other charge"
                                style={{ textAlign: 'right' }}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Refund Amount</label>
                            <input
                                className="form-input"
                                value={refundAmount.toFixed(2)}
                                type="text"
                                readOnly
                                style={{ textAlign: 'right', backgroundColor: '#d1fae5', color: '#059669', fontWeight: '600' }}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Payment Type</label>
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', height: '42px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#475569', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name="PaymentType"
                                        value="1"
                                        checked={selectedPaymentType === '1'}
                                        onChange={handleChange}
                                        style={{ accentColor: '#1e3a5f' }}
                                    />
                                    Cash
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#475569', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name="PaymentType"
                                        value="2"
                                        checked={selectedPaymentType === '2'}
                                        onChange={handleChange}
                                        style={{ accentColor: '#1e3a5f' }}
                                    />
                                    Cheque
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#475569', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name="PaymentType"
                                        value="3"
                                        checked={selectedPaymentType === '3'}
                                        onChange={handleChange}
                                        style={{ accentColor: '#1e3a5f' }}
                                    />
                                    UPI
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentCreate;
